import { create } from "zustand";
import { Solution, PlacementChoiceResponse } from "@gen/travel/v1/search_pb";
import { SearchRequest, SearchResponse } from "@services/travel";
import { Offer, Passport } from "@gen/travel/v1/common_pb";
import { AncillaryToBook, PlacementWish } from "@gen/travel/v1/cart_pb";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";

type OfferSolution = {
  offer: Offer | ExchangeOffer;
  solution: Solution | ExchangeSolution;
  ancillaries?: AncillaryToBook[];
} | null;

type BookingState = {
  searchRequest: SearchRequest | null;
  setSearchRequest: (value: SearchRequest | null) => void;
  outwardOffer: OfferSolution;
  setOutwardOffer: (value: OfferSolution) => void;
  inwardOffer: OfferSolution;
  setInwardOffer: (value: OfferSolution) => void;
  outwardResults: SearchResponse | null;
  setOutwardResults: (value: SearchResponse) => void;
  inwardResults: SearchResponse | null;
  setInwardResults: (value: SearchResponse) => void;
  outwardPlacementChoices: PlacementChoiceResponse | null;
  setOutwardPlacementChoices: (value: PlacementChoiceResponse | null) => void;
  inwardPlacementChoices: PlacementChoiceResponse | null;
  setInwardPlacementChoices: (value: PlacementChoiceResponse | null) => void;
  placementWishes: PlacementWish[];
  // add a placement wish. Overwrite the existing one if the segmentId is the same.
  addPlacementWish: (value: PlacementWish) => void;
  passports: Map<string, Passport>;
  addPassport: (passengerId: string, value: Passport) => void;
  resetBookingForm: () => void;
};

export const useBookingStore = create<BookingState>((set) => ({
  searchRequest: null,
  setSearchRequest: (value) => set({ searchRequest: value }),
  outwardOffer: null,
  setOutwardOffer: (value) => set({ outwardOffer: value }),
  inwardOffer: null,
  setInwardOffer: (value) => set({ inwardOffer: value }),
  outwardResults: null,
  setOutwardResults: (value) => set({ outwardResults: value }),
  inwardResults: null,
  setInwardResults: (value) => set({ inwardResults: value }),
  outwardPlacementChoices: null,
  setOutwardPlacementChoices: (value) =>
    set({ outwardPlacementChoices: value }),
  inwardPlacementChoices: null,
  setInwardPlacementChoices: (value) => set({ inwardPlacementChoices: value }),
  placementWishes: [],
  addPlacementWish: (value) =>
    set((state) => ({
      placementWishes: state.placementWishes.some(
        (w) => w.segmentId === value.segmentId,
      )
        ? state.placementWishes.map((w) =>
            w.segmentId === value.segmentId ? value : w,
          )
        : [...state.placementWishes, value],
    })),
  passports: new Map<string, Passport>(),
  addPassport: (passengerId, value) =>
    set((state) => ({
      passports: state.passports.set(passengerId, value),
    })),
  resetBookingForm: () =>
    set({
      searchRequest: null,
      outwardOffer: null,
      inwardOffer: null,
      outwardResults: null,
      inwardResults: null,
      outwardPlacementChoices: null,
      inwardPlacementChoices: null,
      placementWishes: [],
      passports: new Map<string, Passport>(),
    }),
}));
